import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import type { MainMiniappRoute } from '@miniapp-core/components';
import ServiceDashboard from './ServiceDashboard';
import NavigatorScreen from './NavigatorScreen';
import { RouteStatusContent } from './components/RouteStatusScreen';
import RootMiniappShell from './components/RootMiniappShell';
import type { StaffRolesMap } from './context/StaffRolesContext';
import UiKitPreview from '../core/miniapp/ui-kit/preview/UiKitPreview';

const EventsService = lazy(() => import('../services/events/miniapp/EventsRoot'));
const EventsManager = lazy(() => import('../services/events/miniapp/EventsManagerRoot'));
const DigitalMediaService = lazy(
  () => import('../services/digital-media/miniapp/DigitalMediaRoot'),
);

interface AppRoutesProps {
  activeServices: string[];
  staffRoles: StaffRolesMap;
  servicesLoading: boolean;
  servicesError: string | null;
  retryServices: () => void;
}

interface ServiceRouteGateProps {
  serviceName: string;
  activeServices: string[];
  servicesLoading: boolean;
  servicesError: string | null;
  retryServices: () => void;
  activeRoute?: MainMiniappRoute;
  hasMiniPlayer?: boolean;
  loadingTitle: string;
  children: ReactNode;
}

function ServiceRouteGate({
  serviceName,
  activeServices,
  servicesLoading,
  servicesError,
  retryServices,
  activeRoute,
  hasMiniPlayer = false,
  loadingTitle,
  children,
}: ServiceRouteGateProps) {
  const navigate = useNavigate();

  const shell = (content: ReactNode) => (
    <RootMiniappShell activeRoute={activeRoute} hasMiniPlayer={hasMiniPlayer}>
      {content}
    </RootMiniappShell>
  );

  if (servicesLoading) {
    return shell(
      <RouteStatusContent
        kind="loading"
        title="Загружаем раздел"
        description="Проверяем доступные сервисы и готовим содержимое."
      />,
    );
  }

  if (servicesError) {
    return shell(
      <RouteStatusContent
        kind="error"
        title="Не удалось открыть раздел"
        description={servicesError}
        action={{ label: 'Повторить', onClick: retryServices }}
      />,
    );
  }

  if (!activeServices.includes(serviceName)) {
    return shell(
      <RouteStatusContent
        kind="unavailable"
        title="Раздел пока недоступен"
        description="Этот сервис не подключён для текущего пространства."
        action={{ label: 'На главную', onClick: () => navigate('/') }}
      />,
    );
  }

  return shell(
    <Suspense
      fallback={
        <RouteStatusContent
          kind="loading"
          title={loadingTitle}
          description="Это займёт всего несколько секунд."
        />
      }
    >
      {children}
    </Suspense>,
  );
}

export function AppRoutes({
  activeServices,
  staffRoles,
  servicesLoading,
  servicesError,
  retryServices,
}: AppRoutesProps) {
  const isLocalPreviewHost = ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
  const location = useLocation();
  const digitalMediaActiveRoute: MainMiniappRoute =
    /^\/digital-media\/(navigator|now|topics|states|search)/.test(location.pathname)
      ? 'navigator'
      : 'materials';

  return (
    <Routes>
      {isLocalPreviewHost ? <Route path="/__ui-kit" element={<UiKitPreview />} /> : null}
      <Route
        path="/"
        element={
          <RootMiniappShell activeRoute="home">
            <ServiceDashboard
              activeServices={activeServices}
              staffRoles={staffRoles}
              servicesLoading={servicesLoading}
              servicesError={servicesError}
            />
          </RootMiniappShell>
        }
      />
      <Route path="/navigator/" element={<NavigatorScreen />} />
      <Route
        path="/digital-media/*"
        element={
          <ServiceRouteGate
            serviceName="digital-media"
            activeServices={activeServices}
            servicesLoading={servicesLoading}
            servicesError={servicesError}
            retryServices={retryServices}
            activeRoute={digitalMediaActiveRoute}
            hasMiniPlayer
            loadingTitle="Открываем материалы"
          >
            <DigitalMediaService />
          </ServiceRouteGate>
        }
      />
      <Route
        path="/events/manager/*"
        element={
          <ServiceRouteGate
            serviceName="events"
            activeServices={activeServices}
            servicesLoading={servicesLoading}
            servicesError={servicesError}
            retryServices={retryServices}
            loadingTitle="Открываем инструменты менеджера"
          >
            <EventsManager />
          </ServiceRouteGate>
        }
      />
      <Route
        path="/events/*"
        element={
          <ServiceRouteGate
            serviceName="events"
            activeServices={activeServices}
            servicesLoading={servicesLoading}
            servicesError={servicesError}
            retryServices={retryServices}
            activeRoute="events"
            loadingTitle="Открываем события"
          >
            <EventsService />
          </ServiceRouteGate>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
